import { useApolloClient } from '@apollo/client';
import { CategoryDropdownDocument } from 'lib-knowledge-management-common/graphql';
import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Puts the categories of the given collections into the cache, so their names
 * can be read back by id.
 *
 * `categories(limit:)` is capped at 50 by the API and an organisation can hold
 * far more, so a collection's tree is read instead: it covers every category a
 * draft in it could be filed under, and drafts reference only a handful of
 * collections between them.
 *
 * Returns a counter that changes when a batch lands. Callers that derive names
 * from the cache should keep it among their dependencies, since a fragment read
 * is not reactive on its own.
 */
export function useSeedCategoryTitles(
  collectionIds: readonly (string | null | undefined)[],
): number {
  const client = useApolloClient();
  const [seeded, setSeeded] = useState(0);
  // Asked for once each, however often the list re-renders.
  const requested = useRef(new Set<string>());

  const ids = useMemo(
    () => Array.from(new Set(collectionIds.filter((id): id is string => !!id))),
    [collectionIds],
  );

  useEffect(() => {
    const fresh = ids.filter((id) => !requested.current.has(id));
    if (!fresh.length) return;

    fresh.forEach((id) => requested.current.add(id));

    Promise.all(
      fresh.map((collectionId) =>
        client
          .query({
            query: CategoryDropdownDocument,
            variables: { collectionId },
            fetchPolicy: 'cache-first',
          })
          // A collection that cannot be read leaves its drafts without a
          // category name, which is not worth failing the list over. Its id
          // comes back off the asked-for set so the next run can try again —
          // left in, one transient failure would drop that collection's
          // category names for the rest of the session.
          .catch(() => {
            requested.current.delete(collectionId);
            return undefined;
          }),
      ),
    ).then(() => setSeeded((count) => count + 1));
  }, [client, ids]);

  return seeded;
}
